import type { MaybeRefOrGetter } from 'vue';
import type { AnyItem, CompositePrimaryKey, RelationHandlers } from '#layers/data-model';
import type { ResolvedMonospaceCollection, ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import { reactiveComputed } from '@vueuse/core';
import { computed, toValue } from 'vue';
import { noop } from '#layers/base';
import { isNullishOrEmpty } from '#layers/base/utils/iterable';
import { getRelationConstraint, getRelationPrimitiveFields } from '#layers/data-model';
import { pickByPrimitiveFields } from '#layers/data-model/utils/item';
import { getItemPrimaryKey } from '#layers/data-model/utils/primary-key';
import { injectFormBlockContext } from '~/composables/form-block/form-context';
import { createRelationFieldStateToManyHandler } from '~/utils/fields/relation-to-many-handlers';
import { createRelationFieldStateToOneInCreateHandler } from '~/utils/fields/relation-to-one-handlers-in-create';
import { createRelationFieldStateToOneInUpdateHandler } from '~/utils/fields/relation-to-one-handlers-in-update';

export const createdItemKey = Symbol('createdItemKey');
export const originalItemKey = Symbol('originalItemKey');

export type CreateItem = AnyItem & { [createdItemKey]: string };

export function isCreatedItem(item: AnyItem) {
	return createdItemKey in (item ?? {});
}

export function getOriginalItemKey(item: AnyItem) {
	return item[originalItemKey];
}

export function markCreatedItem(item: AnyItem, originalItem: AnyItem) {
	return { ...item, [createdItemKey]: originalItem[createdItemKey] };
}

/**
 * Display-only marker (not persisted — Symbols drop out of serialization) flagging a
 * `_connect` to an already-existing target that should badge as "created" rather than
 * "connected" (e.g. a file uploaded in-form). The record already exists, so the junction
 * stays a `_connect`; only the badge changes.
 */
export const connectedAsCreatedKey = Symbol('connectedAsCreatedKey');

export function markConnectedAsCreated(item: AnyItem) {
	return { ...item, [connectedAsCreatedKey]: true };
}

export function isConnectedAsCreated(item: AnyItem) {
	return connectedAsCreatedKey in (item ?? {});
}

/**
 * @internal
 */
export function useRelationFieldState(field: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField>) {
	const formBlockContext = injectFormBlockContext(null);
	const computedField = computed(() => toValue(field));

	const fieldValue = computed<any>({
		get() {
			return formBlockContext?.state.value?.$getRaw(computedField.value.apiName);
		},
		set(value) {
			if (!formBlockContext?.state.value) return;
			formBlockContext.state.value[computedField.value.apiName] = value;
		},
	});

	const defaultHandlers: RelationHandlers = {
		handleCreate: noop,
		handleUndoCreate: noop,
		handleConnect: noop,
		handleUndoConnect: noop,
		handleDisconnect: noop,
		handleUndoDisconnect: noop,
		handleDelete: noop,
		handleUndoDelete: noop,
		handleUpdate: noop,
	};

	const specificHandlers = computed(() => {
		if (computedField.value.isList) {
			return createRelationFieldStateToManyHandler(toValue(field), fieldValue);
		}
		else if (formBlockContext?.isCreate.value) {
			return createRelationFieldStateToOneInCreateHandler(toValue(field), fieldValue, formBlockContext);
		}
		else {
			return createRelationFieldStateToOneInUpdateHandler(toValue(field), fieldValue, formBlockContext);
		}
	});

	const handlers = reactiveComputed(() => ({
		...defaultHandlers,
		...specificHandlers.value,
	}));

	const referencedItemKey = computed(() => {
		if (!formBlockContext) return null;

		const { thisSideConstrained } = getRelationConstraint(computedField.value);

		if (!thisSideConstrained && formBlockContext.itemKey.value) {
			// O2M: use stable primary key from form context
			return formBlockContext.itemKey.value;
		}

		// M2O or create mode: use live form state
		if (!formBlockContext.state.value) return null;

		return toReferencedItemKey(formBlockContext.state.value, computedField.value);
	});

	return {
		fieldValue: computed(() => fieldValue.value),
		referencedItemKey,
		handleCreate: (payload: AnyItem) => handlers.handleCreate(payload),
		handleUndoCreate: (payload: AnyItem) => handlers.handleUndoCreate(payload),
		handleConnect: (key: CompositePrimaryKey) => handlers.handleConnect(key),
		handleUndoConnect: (key: CompositePrimaryKey) => handlers.handleUndoConnect(key),
		handleDisconnect: (key: CompositePrimaryKey) => handlers.handleDisconnect(key),
		handleUndoDisconnect: (key: CompositePrimaryKey) => handlers.handleUndoDisconnect(key),
		handleDelete: (key: CompositePrimaryKey) => handlers.handleDelete(key),
		handleUndoDelete: (key: CompositePrimaryKey) => handlers.handleUndoDelete(key),
		handleUpdate: (originalKey: CompositePrimaryKey | null, payload: AnyItem) => handlers.handleUpdate(originalKey, payload),
	};
}

export function toReferencedItemKey(item: AnyItem, relationField: ResolvedMonospaceSingleRelationField) {
	const { constraint, thisSideConstrained } = getRelationConstraint(relationField);
	const { fields } = getRelationPrimitiveFields(relationField);

	if (!item || isNullishOrEmpty(fields)) return null;

	const itemKeys = pickByPrimitiveFields(item, fields);

	if (thisSideConstrained) {
		// map the item keys to the referenced field names
		const mappedKeys: CompositePrimaryKey = {};

		for (const { constrainedField, referencedField } of constraint!.fieldMappings) {
			mappedKeys[referencedField!.apiName] = itemKeys[constrainedField!.apiName];
		}

		return mappedKeys;
	}
	else {
		return itemKeys;
	}
}

export function fromReferencedItemKey(itemKey: CompositePrimaryKey, relationField: ResolvedMonospaceSingleRelationField) {
	const { constraint, thisSideConstrained } = getRelationConstraint(relationField);
	const { oppositeFields } = getRelationPrimitiveFields(relationField);

	if (!itemKey || isNullishOrEmpty(oppositeFields)) return null;

	const itemKeys = pickByPrimitiveFields(itemKey, oppositeFields);

	if (thisSideConstrained) {
		// map the item keys to the referenced field names
		const mappedKeys: CompositePrimaryKey = {};

		for (const { constrainedField, referencedField } of constraint!.fieldMappings) {
			mappedKeys[constrainedField!.apiName] = itemKeys[referencedField!.apiName];
		}

		return mappedKeys;
	}
	else {
		return itemKeys;
	}
}

export function verifyItemKeys(originalKey: CompositePrimaryKey | null, payload: AnyItem, collection: ResolvedMonospaceCollection) {
	if ((!originalKey || Object.keys(originalKey).length === 0) && !payload?.[createdItemKey]) {
		throw new Error('The item is missing a createdItemKey');
	}

	if (originalKey) {
		const referenceKeys = Object.keys(getItemPrimaryKey(payload, collection));
		const hasAllKeys = referenceKeys.every((key) => Object.prototype.hasOwnProperty.call(originalKey, key));

		if (!hasAllKeys) {
			throw new Error('The item is missing a primary key');
		}
	}
}
